import React from "react"
import moment from "moment"
import omit from "lodash/omit"

import GlobalActionTypes from "global-action-types"
import { segue, loadPeople, loadSupporters } from "global-action-creators"
import {
    resourceGetDetail,
    resourceGetList,
    resourcePatchAction,
    resourcePatchDetail,
    resourcePostList,
} from "shared/djangio/action-creators"
import * as djangioActionCreators from "shared/djangio/action-creators"

// Outbox Imports
import { getDefaultBulkEmailValues } from "outbox/reducer"
/*eslint import/namespace: ['error', { allowComputed: true }]*/
import * as bulkEmailSelectors from "shared/outbox/selectors"
// all the action type and the groupings
import bulkEmailActions, {
    getAiSuggestionActions,
    getBulkEmailPieActions,
    getABVariantsPieActions,
    updatePlaceholdersActions,
    patchBulkEmailPieActions,
    postBulkEmailPieActions,
    testingBulkEmailPieActions,
    sendingBulkEmailPieActions,
    cancelBulkEmailPieActions,
    duplicateAttachmentsActions,
    validatePlaceholdersActions,
    getEmailPreviewActions,
    patchBulkEmailM2MPieActions,
} from "shared/outbox/action-types"

import searchifyActionTypes from "shared/search/searchify/action-types"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"

import { generateStaticUrl, humanFileSize, makeId } from "shared/imports/sharedHelperFunctions"
import swal from "sweetalert"
import swalConfigs from "app/static/frontend/swalConfigs"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { SCHEDULER_TYPES } from "shared/imports/sharedConstants"
import { parseTestEmailInput } from "outbox/helpers"
import { outboxConstants } from "shared/outbox/constants"
import { wrapFiltersInForSearchify } from "shared/search/helperFunctions"
import SendConfirmation from "app/static/frontend/outbox/components/Alerts/SendConfirmation"
import ABSendConfirmation from "app/static/frontend/outbox/components/Alerts/ABSendConfirmation"
import { oneLine } from "shared/imports/stringFunctions"
import { getUserdataField } from "shared/imports/userdataFunctions"

const { ABTest } = DjangIO.app.userdata.outbox.models.ab_test
const { BulkEmail } = DjangIO.app.userdata.outbox.models.bulk_email
const { ABTestVariable } = DjangIO.app.userdata.outbox.types
const { Attachment } = DjangIO.app.userdata.models

// CONSTANTS
export const FORCE_SUCCESS_TIMER = 10000
export const SWAL_SUCCESS_TIMER = 3000
export const SEND_REDIRECT_TIMER = 2100

/**
 * Action Creator that resets the state
 */
export const resetBulkEmail = (bulkEmailId) => (dispatch) =>
    dispatch({
        type: bulkEmailActions.RESET_BULK_EMAIL,
        bulkEmailId,
    })

/**
 *********************
 * NAVIGATION THINGS *
 *********************
 */

/* Action creator for going to the main page */
export const segueToOutboxOverview = (tab = "sent") => segue(`/outbox/${tab}`)

/**
 * Action creator for going to the detial page
 *
 * @param {Number} bulkEmailId the dbid of the BulkEmail
 */
export const segueToEmailDetail = (bulkEmailId) => segue(`/outbox/details/${bulkEmailId}/`)

/**
 * Action creator for going to the compose page
 *
 * @param {Number} bulkEmailId the dbid of the BulkEmail
 */
export const segueToBulkEmail = (bulkEmailId) => segue(`/outbox/compose/${bulkEmailId}`)

/**
 * Action Creator for Going to outbox with args
 *
 * @param {Number}  bulkEmailId the bulk email id
 * @param {Array}   toSupporters array of supporter ids
 * @param {Array}   toPeople array of person id's
 * @param {Boolean} abtest whether this email will be an ABTest
 * @returns {Promise} promise of the new page
 */
export function segueToOutbox({ bulkEmailId, toSupporters, toPeople, isABTest } = {}) {
    // If we already have a DB ID, then any passed args will be discarded anyways
    if (bulkEmailId) {
        return segueToBulkEmail(bulkEmailId)
    }

    return (dispatch) => {
        // make sure the props are wiped
        dispatch(resetBulkEmail())

        let urlString = "/?"
        if (toSupporters) {
            urlString += `toSupporters=[${toSupporters}]&`
        }
        if (toPeople) {
            urlString += `toPeople=[${toPeople}]`
        }
        if (isABTest) {
            urlString += `is_abtest=${isABTest}`
        }

        const prevPageUrl = window.location.pathname
        dispatch({
            type: GlobalActionTypes.PUSH_PAGE,
            nextPage: { prevPageUrl },
        })

        dispatch(segue(`/outbox/compose${urlString}`))
        dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.outbox_click_new_email))
    }
}

/**
 **************************************
 * GENERALIZED ACTION CREATORS (CRUD) *
 **************************************
 */

/**
 * Action creator for creating a bulkemail
 *
 * @param {object} [kwargs={}] update args (if overwriting defaults)
 * @param {object} [payload={}] extra args to pass if needed
 * @returns {Promise<object>} promise from post
 */
export const createBulkEmail = (kwargs = {}, payload = {}) =>
    resourcePostList(
        BulkEmail.objects.filter({ dehydrate_extra: ["abtest_children"] }),
        postBulkEmailPieActions,
        { ...getDefaultBulkEmailValues(), ...kwargs },
        payload,
    )

/**
 * Action creator for loading a bulkemail
 *
 * @param {String} bulkEmailId - The database ID of the BulkEmail
 * @returns {Promise<object>} returns the request promise
 */
export const loadBulkEmail = (bulkEmailId) =>
    resourceGetDetail(
        BulkEmail.objects
            .full_dehydrate("attachments")
            .filter({ dehydrate_extra: ["creator", "truncation_data", "abtest_children"] }),
        getBulkEmailPieActions,
        bulkEmailId,
        { bulkEmailId },
    )

export const loadABTestVariants = (bulkEmailId) => (dispatch, getState) => {
    const state = getState()
    const abTestChildrenIds = bulkEmailSelectors.selectABTestChildrenIds(state)

    if (abTestChildrenIds) {
        return dispatch(
            resourceGetList(
                BulkEmail.objects.filter({
                    // Drop these default filters
                    variant_of__isnull: undefined,
                    winner_of__isnull: undefined,
                    // Ensure the html and json_content fields are included - even though this is a list request
                    force_content: true,
                    dehydrate_extra: ["creator", "truncation_data", "attachments"],
                }),
                getABVariantsPieActions,
                { id__in: abTestChildrenIds.join(",") },
                { bulkEmailId },
            ),
        )
    }

    return undefined
}

export const setActiveBulkEmail = (bulkEmailId) => (dispatch) =>
    dispatch({
        type: bulkEmailActions.SET_ACTIVE_BULK_EMAIL_ID,
        bulkEmailId,
    })

/**
 * @name syncABTest
 *
 * Ensures that the non-ABTested fields are sync'd between the Base BulkEmail and the variants.
 * ex: If you are testing Subjects, any updates to the html_content field should be applied to both variants.
 *
 * Should only be called when a Base BulkEmail is being updated.
 * ie: isABTest is true and there is no bulkEmailId passed.
 *
 * @param {object} payload Map of fields being updated
 */
export const syncABTest = (payload) => (dispatch, getState) => {
    const state = getState()
    const variantIds = bulkEmailSelectors.selectABTestVariantIds(state).toJS()
    const testVariable = bulkEmailSelectors.selectABTestVariable(state)

    // Get the fields we expect to be different between variants
    const testFields = ABTestVariable.by_value(testVariable).fields

    // And omit them from the passed in payload
    const newPayload = omit(payload, testFields)

    // If there are any fields left, we want to apply them to each variant
    if (Object.keys(newPayload).length) {
        variantIds.forEach((variantId) =>
            dispatch({
                type: bulkEmailActions.UPDATE_BULK_EMAIL,
                bulkEmailId: variantId,
                payload: newPayload,
            }),
        )
    }
}

/**
 * @name updateBulkEmail
 *
 * Action creator for modifying the state of a BulkEmail.
 *
 * By default, this function will update the active BulkEmail determined by the bulkEmailId in the outbox slice.
 * If a bulkEmailId is passed in, it will modify that slice instead.
 *
 * If we're working on the active BulkEmail, and email is an ABTest,
 * we'll also apply the payload to each variant associated with it - less the field(s) currently being tested!
 *
 * @param {object} payload - key/values for updating state
 * @param {string} [bulkEmailId] - The database ID of the BulkEmail
 */
export const updateBulkEmail = (payload, bulkEmailId) => (dispatch, getState) => {
    const state = getState()
    // Select the activeBulkEmail and check if it is an ABTest
    const isABTest = bulkEmailSelectors.selectIsABTest(state)

    // For ABTests, we want to keep all fields - except the ones being tested - in sync
    // If there is no bulkEmailId, we're editing the base BulkEmail
    if (isABTest && bulkEmailId === undefined) {
        dispatch(syncABTest(payload))
    }

    return dispatch({
        type: bulkEmailActions.UPDATE_BULK_EMAIL,
        bulkEmailId: bulkEmailId || bulkEmailSelectors.getId(state),
        payload,
    })
}

/**
 * Action creator for patching a bulkemail
 *
 * @param {Number} [id=null] dbId of the bulk_email
 * @param {object} [kwargs={}] update args (if overwriting store)
 * @param {object} [payload={}] extra args to pass
 * @param {String} [updateAction=''] action to run on patch
 * @returns {Promise<object>} promise from patch
 */
export const patchBulkEmail =
    (id = null, kwargs = {}, payload = {}, updateAction = "") =>
    async (dispatch, getState) => {
        const state = getState()
        const bulkEmailId = id || bulkEmailSelectors.getId(state)

        // if it is sending or isn't a draft block the patch
        if (bulkEmailSelectors.isDraft(state) === false || bulkEmailSelectors.isSending(state)) {
            // See if this check exists on the API/model layer
            return dispatch({
                type: bulkEmailActions.AUTOSAVE_BULK_EMAIL_BLOCKED,
                bulkEmailId,
            })
        }

        // Assuming we're not doing a targeted PATCH,
        // iterate through any ABTest variants and PATCH those first
        if (!id) {
            bulkEmailSelectors.selectABTestVariants(state).forEach((variant) =>
                dispatch(
                    resourcePatchDetail(
                        updateAction
                            ? BulkEmail.objects.action(updateAction).full_dehydrate("attachments")
                            : BulkEmail.objects.full_dehydrate("attachments"),
                        patchBulkEmailPieActions,
                        variant.get("id"),
                        { ...bulkEmailSelectors.selectVariantPatchableFields(variant.get("id"))(state) },
                        {
                            ...payload,
                            bulkEmailId: variant.get("id"),
                        },
                    ),
                ),
            )
        }

        return dispatch(
            resourcePatchDetail(
                updateAction
                    ? BulkEmail.objects.action(updateAction).full_dehydrate("attachments")
                    : BulkEmail.objects.full_dehydrate("attachments"),
                patchBulkEmailPieActions,
                bulkEmailId,
                { ...bulkEmailSelectors.selectPatchableFields(state), ...kwargs },
                {
                    ...payload,
                    bulkEmailId,
                },
            ),
        )
    }

/**
 * autosaveBulkEmail
 *
 * - saves the bulk email if there is an id, if it is not a draft and if
 *   it isn't currently sending, saving, or previewing
 *
 * @name autosaveBulkEmail
 * @function
 */
export const autosaveBulkEmail = () => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)

    // get the time of last form change and last autosave
    const lastSave = bulkEmailSelectors.getLastAutosavedMoment(state)
    const lastChange = bulkEmailSelectors.getLastUpdatedMoment(state)

    // only autosave if the form was updated more recently than the last autosave
    if (lastChange >= lastSave) {
        dispatch({
            bulkEmailId,
            type: bulkEmailActions.AUTOSAVE_BULK_EMAIL_START,
        })

        // if the email is updating or if there isn't an email - don't save
        if (
            bulkEmailSelectors.hasRequestInProgress(state) ||
            bulkEmailSelectors.getId(state) === 0 ||
            bulkEmailSelectors.isDraft(state) === false
        ) {
            dispatch({
                bulkEmailId,
                type: bulkEmailActions.AUTOSAVE_BULK_EMAIL_BLOCKED,
            })

            return
        }

        // otherwise - patch the bulk email
        await dispatch(patchBulkEmail())
        dispatch({
            bulkEmailId,
            type: bulkEmailActions.AUTOSAVE_BULK_EMAIL_SUCCESS,
        })
    }
}

/**
 *  Update and Patch in the same action creator. Also allows calling update_selection sintead of a normal patch.
 *  Used to update the from name so the placeholder appears properly in FromReplyToSelect
 *  We dispatch updateBulkEmail first to optimistically render the change
 *  and then update the store with the response from patchBulkEmail
 *
 * @param {object} payload - key/values for updating state
 * @param {boolean} [payload.shouldUpdateSelection=false] - Whether should_update_selection action should also be fired
 * @param {string} payload.bulkEmailId - The database ID of the BulkEmail
 * @param {object} payload.props - The bulk of the key/value pairs
 */
export const updateAndPatchBulkEmail =
    ({ shouldUpdateSelection = false, bulkEmailId, ...props }) =>
    async (dispatch, getState) => {
        await dispatch(updateBulkEmail(props, bulkEmailId))

        if (shouldUpdateSelection) {
            // eslint-disable-next-line no-use-before-define
            await dispatch(updateSelection(bulkEmailSelectors.selection(getState())))
        } else {
            dispatch(patchBulkEmail())
        }
    }

/**
 * Action creator for associating a BulkEmail with a M2M collection
 *
 * @param {String} [id=null] - The database ID of the BulkEmail
 * @param {String} field - The M2M field being updated
 * @param {object[]} [items=[]] - Array of Projects or Campaigns to be linked to the BulkEmail
 */
export const patchBulkEmailM2M =
    (id = null, field, items = []) =>
    (dispatch, getState) => {
        const state = getState()
        const bulkEmailId = id || bulkEmailSelectors.getId(state)

        // the payload is passed as urls into the store, so we format it so it can
        // be properly merged in the reducer
        const payload = {
            [field]: items.map((item) => item.resource_uri),
        }

        dispatch(
            resourcePatchDetail(
                // call the appropriate action
                BulkEmail.objects.action(`update_${field}`),
                patchBulkEmailM2MPieActions,
                bulkEmailId,
                // pass the values by their id to the server
                { [field]: items.map((item) => item.id) },
                {
                    ...payload,
                    bulkEmailId,
                    field,
                },
            ),
        )

        return dispatch(updateBulkEmail(payload, bulkEmailId))
    }

/**
 * Action creator for associating a BulkEmail with a set of Issues
 *
 * @param {String} [id=null] - The database ID of the BulkEmail
 * @param {object[]} [projects=[]] - Array of Projects to be linked to the BulkEmail
 */
export const patchBulkEmailProject =
    (id = null, projects = []) =>
    (dispatch) => {
        dispatch(patchBulkEmailM2M(id, "projects", projects))
    }

/**
 * Method checks if projects are selected when organization setting deems it is required.
 * Only called via prepareSendTestEmail and confirmEmail
 *
 * @name hasValidateBulkEmailProject
 * @function
 * @returns {boolean} returns valid or not
 */
export const hasValidateBulkEmailProject = () => (dispatch, getState) => {
    const state = getState()
    const requireProject = getUserdataField("organization_require_issue_on_outbox")
    const selectedProjects = bulkEmailSelectors.projectUris(state)
    if (requireProject && selectedProjects.length === 0) {
        return false
    }
    return true
}

/**
 * Creates a BulkEmailCampaign object to associate the given BulkEmail with a set of Campaigns
 *
 * @param {number} [id] - ID of the given BulkEmail
 * @param {object[]} [campaigns=[]] - List of Campaigns to be linked
 */
export const patchBulkEmailCampaigns =
    (id, campaigns = []) =>
    (dispatch) => {
        dispatch(patchBulkEmailM2M(id, "campaigns", campaigns))
    }

/**
 ****************
 * PLACEHOLDERS *
 ****************
 */

/**
 * Action Creator that updates the usable placeholders for a bulkemail
 *
 * @param {Number} bulkEmailId - bulkemail id
 * @param {object} [filter={}] - Add extra filters to the network request.
 *      Typically (exclusively) used with { is_template: true }
 * @returns {Promise<object>} returns the promise for the request
 */
export const updatePlaceholders = (bulkEmailId, filter = {}) =>
    resourceGetDetail(
        BulkEmail.objects.action("get_placeholders_for_recipients").filter(filter),
        updatePlaceholdersActions,
        bulkEmailId,
        { bulkEmailId },
    )

/**
 * Method that validates placeholders. Only called via validPlaceholderPreview and validateBulkEmail
 *
 * @name validatePlaceholders
 * @function
 * @returns {Promise<object>} returns response whether valid
 */
export const validatePlaceholders = () => (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)
    return dispatch(
        resourcePatchDetail(
            BulkEmail.objects.action("are_placeholders_valid_for_message"),
            validatePlaceholdersActions,
            bulkEmailId,
            { ...bulkEmailSelectors.selectPatchableFields(state) },
            { bulkEmailId },
        ),
    )
}

/**
 * Method that validates placeholders. Only called via validPlaceholderPreview and validateBulkEmail
 *
 * @name validatePlaceholdersForVariant
 * @function
 * @param {number} [variantId] - The BulkEmail to validate
 * @returns {Promise<object>} returns response whether valid
 */
export const validatePlaceholdersForVariant = (variantId) => (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = variantId || bulkEmailSelectors.getId(state)

    return dispatch(
        resourcePatchDetail(
            BulkEmail.objects.action("are_placeholders_valid_for_message"),
            validatePlaceholdersActions,
            bulkEmailId,
            { ...bulkEmailSelectors.selectVariantPatchableFields(bulkEmailId)(state) },
            { bulkEmailId },
        ),
    )
}

/**
 * Calls validatePlaceholders, and displays any errors to the user
 *
 * @returns {Promise} returns the promise for the request
 */
export const validPlaceholderPreview = (variantId) => async (dispatch) => {
    try {
        const { valid, errors, is_format_error } = await dispatch(
            variantId ? validatePlaceholdersForVariant(variantId) : validatePlaceholders(),
        )
        // if not valid - sweetalert
        if (!valid) {
            qual.fire({
                title: "Warning",
                icon: "puzzle-piece",
                iconFamily: "fas",
                text: is_format_error
                    ? `Your message has the invalid placeholders:\n${errors.join(",\n")}`
                    : "Your message has invalid placeholders. Click preview to view them",
                actions: { ok: "OK" },
            })
        }
    } catch (e) {
        // show error
        qual.close()
        const message = e.response && e.response.data && e.response.data.message
        BACKENDERROR(e, swalConfigs.outboxFailedPreview(message))
    }
}

/**
 ***************************
 * PREVIEW ACTION CREATORS *
 ***************************
 */

/**
 * Action Creator that resets the preview pane
 *      (as well as the validity of the placeholders)
 *  it is called when:
 *      - The selection updates
 *      - When the subject changes
 *      - When the html changes / a template is loaded
 *
 * @name resetPreviews
 * @function
 * @param {number} [variantId] - Optionally, provide the BulkEmail to reset
 */
export const resetPreviews = (variantId) => (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = variantId || bulkEmailSelectors.getId(state)
    const isABTest = bulkEmailSelectors.selectIsABTest(state)

    // If we're not targeting a specific variantId, then reset all of them
    if (isABTest && !variantId) {
        const variantIds = bulkEmailSelectors.selectABTestVariantIds(state)

        variantIds.forEach((variant) =>
            dispatch({
                type: bulkEmailActions.RESET_BULK_EMAIL_PREVIEWS,
                bulkEmailId: variant,
            }),
        )
    }

    dispatch({
        type: bulkEmailActions.RESET_BULK_EMAIL_PREVIEWS,
        bulkEmailId,
    })
}

/**
 * Action Creator that generates the previews
 *
 * @name generatePreviews
 * @function
 */
export const generatePreviews = (variantId) => (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = variantId || bulkEmailSelectors.getId(state)

    if (variantId) {
        return dispatch(
            resourcePatchDetail(
                BulkEmail.objects.action("generate_previews", {
                    startIndex: bulkEmailSelectors.getPreviewIndexABTest(bulkEmailId)(state),
                    use_personal_email: bulkEmailSelectors.usePersonalEmail(state),
                }),
                getEmailPreviewActions,
                bulkEmailId,
                {
                    subject: bulkEmailSelectors.getABTestedSubject(bulkEmailId)(state),
                    html_content: bulkEmailSelectors.selectVariantHtmlContent(bulkEmailId)(state),
                },
                { bulkEmailId },
            ),
        )
    }

    // dispatch the start
    return dispatch(
        resourcePatchDetail(
            BulkEmail.objects.action("generate_previews", {
                startIndex: bulkEmailSelectors.getPreviewIndex(state),
                use_personal_email: bulkEmailSelectors.usePersonalEmail(state),
            }),
            getEmailPreviewActions,
            bulkEmailId,
            {
                subject: bulkEmailSelectors.subject(state),
                html_content: bulkEmailSelectors.htmlContent(state),
            },
            { bulkEmailId },
        ),
    )
}

/**
 *****************************
 * AB Test action creators *
 *****************************
 */

/**
 * Update the nested ABTest object in a BulkEmail
 *
 * @param {object} payload - key/values for updating state in the abtest object
 * @param {string} bulkEmailId - The database ID of the BulkEmail
 */
export const updateABTest = (payload, bulkEmailId) => (dispatch, getState) =>
    dispatch({
        type: bulkEmailActions.UPDATE_ABTEST,
        bulkEmailId: bulkEmailId || bulkEmailSelectors.getId(getState()),
        payload,
    })

/**
 *****************************
 * RECIPIENT ACTION CREATORS *
 *****************************
 */

/**
 * Action Creator that is fired when the recipients change for a message
 *
 * @param {object} recipientsObject object with keys of selection or excludes
 * @returns {Promise}
 */
export const updateSelection = (recipientsObject) => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)

    // update state
    // trigger the start
    const updateDict = {}

    if ("selection" in recipientsObject) {
        /**
         * add the always_forced_region field to a selection object
         * to create the correct safedsearch on the backend. This makes sure
         * the queryset is localized to whatever region the user was in when
         * they created the search object
         */
        let data = recipientsObject.selection

        data = data.map((selectionObj) => {
            // only add if the selection is from search
            if (!selectionObj.always_forced_regions && selectionObj.advSearchParams) {
                return {
                    ...selectionObj,
                    always_forced_regions: Userdata.current_regions_values,
                }
            }

            return selectionObj
        })

        updateDict.selection_json = { data }
    }

    if ("excludes" in recipientsObject) {
        let data = recipientsObject.excludes

        data = data.map((excludesObj) => {
            // only add if the selection is from search
            if (!excludesObj.always_forced_regions && excludesObj.advSearchParams) {
                return {
                    ...excludesObj,
                    always_forced_regions: Userdata.current_regions_values,
                }
            }

            return excludesObj
        })

        updateDict.excludes_json = { data }
    }

    dispatch({
        type: bulkEmailActions.UPDATE_RECIPIENTS_START,
        recipientsObject: updateDict,
        bulkEmailId,
    })

    // trigger the update
    const response = await dispatch(patchBulkEmail(bulkEmailId, {}, {}, "update_selection"))

    // trigger the success event
    dispatch({
        type: bulkEmailActions.UPDATE_RECIPIENTS_SUCCESS,
        bulkEmailId,
    })

    // the placeholders should be updated
    dispatch(updatePlaceholders(response.id))

    // the previews should be reset
    dispatch(resetPreviews())
}

/**
 ***************
 * ATTACHMENTS *
 ***************
 */

/**
 * Action creator for validating attachments for standard BulkEmails and AB
 * Test Variant BulkEmails
 * Check if newly added attachments can be added
 * Currently checks only that the combined size of all attachments
 * doesn't exceed max
 *
 * @param {object[]} newAttachments new attachments to add
 * @param {Number} variantId only used for AB Test Variant BulkEmails
 * @returns {Boolean} true if the new attachments are all valid, false otherwise
 */
export const validateNewAttachments = (newAttachments, variantId) => async (dispatch, getState) => {
    let existingAttachments = null
    const state = getState()
    if (variantId) {
        // AB Test Variant BulkEmail
        existingAttachments = bulkEmailSelectors.getAttachmentsABTest(variantId)(state, variantId)
    } else {
        // standard BulkEmail
        existingAttachments = bulkEmailSelectors.getAttachments(state)
    }

    const existingFileSize = existingAttachments.reduce((totalBytes, file) => totalBytes + file.size, 0)
    const totalFileSize = Array.from(newAttachments).reduce(
        (totalBytes, file) => totalBytes + file.size,
        existingFileSize,
    )

    if (totalFileSize >= outboxConstants.ATTACHMENT_MAX_SIZE) {
        await qual.fire({
            title: "Attachments Could Not Be Added",
            icon: "exclamation-triangle",
            iconFamily: "fas",
            text:
                `The total size of all attachments would exceed` +
                ` ${humanFileSize(outboxConstants.ATTACHMENT_MAX_SIZE)}.` +
                ` Please remove or compress some attachments and try again.`,
            actions: { ok: "OK" },
        })

        return false
    }

    return true
}

/**
 * Action Creator that duplicates attachments
 *
 * @param {Array} attachments array of attachment ids
 * @returns {Promise} returns promise that has the new attachment objects
 */
export const duplicateAttachments = (attachments, id) => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = id || bulkEmailSelectors.getId(state)

    const response = await dispatch(
        resourceGetList(
            Attachment.objects.action("duplicate_attachments"),
            duplicateAttachmentsActions,
            { id__in: attachments },
            { bulkEmailId },
        ),
    )

    return response
}

/**
 * Action creator for adding attachments for standard BulkEmails and AB
 * Test Variant BulkEmails
 *
 * @param {object[]} newAttachments - new attachment objects
 * @param {Number} variantId only used for AB Test Variant BulkEmails
 */
export const addAttachments = (newAttachments, variantId) => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = variantId || bulkEmailSelectors.getId(state)
    let attachments = null

    if (variantId) {
        attachments = bulkEmailSelectors.getAttachmentsABTest(variantId)(state, variantId)
    } else {
        attachments = bulkEmailSelectors.getAttachments(state)
    }

    // make sure it isn't a duplicate - there should be no other
    // emails with this
    const dedupedAttachments = newAttachments.filter(
        (newAttachment) => !attachments.some((attachment) => attachment.id === newAttachment.id),
    )

    if (dedupedAttachments.length) {
        // Attachments are One to One, so we need to duplicate the attachment for each variant
        const isABTest = bulkEmailSelectors.selectIsABTest(state)
        if (isABTest && variantId === undefined) {
            const variantIds = bulkEmailSelectors.selectABTestVariantIds(state).toJS()
            const attachmentIds = dedupedAttachments.map((attachment) => attachment.id)

            await variantIds.forEach(async (variant) => {
                const existingAttachments = bulkEmailSelectors.getAttachmentsABTest(variant)(state)

                const response = await dispatch(duplicateAttachments(attachmentIds, variant))

                dispatch(
                    updateBulkEmail(
                        {
                            attachments: [...response.attachments, ...existingAttachments],
                        },
                        variant,
                    ),
                )
            })
        }

        dispatch(updateBulkEmail({ attachments: attachments.concat(newAttachments) }, bulkEmailId))
    }
}

/**
 * Action creator for deleting attachments for standard BulkEmails and AB
 * Test Variant BulkEmails
 *
 * @param {object} event the event object
 * @param {Number} attachmentId the attachment
 * @param {Number} variantId only used for AB Test Variant BulkEmails
 */
export const deleteAttachment = (event, attachmentId, variantId) => {
    event.preventDefault()
    event.stopPropagation()
    return async (dispatch, getState) => {
        const deleteFn = (_attachmentId, _attachments, _variantId) =>
            // eslint-disable-next-line no-undef
            $.ajax({
                url: `/outbox/delete/${_attachmentId}/`,
                type: "POST",
                cache: false,
                processData: false,
                contentType: "application/json",
                data: JSON.stringify({}),
                success: () =>
                    dispatch(
                        updateBulkEmail(
                            { attachments: _attachments.filter((attachment) => attachment.id !== _attachmentId) },
                            _variantId,
                        ),
                    ),
                error(jqXHR, textStatus) {
                    BACKENDERROR(textStatus)
                },
            })

        const state = getState()
        const bulkEmailId = variantId || bulkEmailSelectors.getId(state)
        const isABTest = bulkEmailSelectors.selectIsABTest(state)
        let attachments = null

        if (variantId) {
            attachments = bulkEmailSelectors.getAttachmentsABTest(variantId)(state, variantId)
        } else {
            attachments = bulkEmailSelectors.getAttachments(state)
        }

        // If this is an ABTest, delete the linked attachment from the variants
        if (isABTest && variantId === undefined) {
            const variantIds = bulkEmailSelectors.selectABTestVariantIds(state).toJS()

            await variantIds.forEach(async (variant) => {
                const existingAttachments = bulkEmailSelectors.getAttachmentsABTest(variant)(state)
                const attachmentToDelete = existingAttachments.find(
                    (attachment) => attachment.duplicated_from === attachmentId,
                )

                await deleteFn(attachmentToDelete.id, existingAttachments, variant)
            })
        }

        // Delete the attachment from the base bulk email
        deleteFn(attachmentId, attachments, bulkEmailId)
    }
}

/**
 * Duplicates the attachments and then saves the BulkEmail
 * @param {Number[]} attachmentIds
 */
export const duplicateAttachmentsAndPatch = (attachmentIds, bulkEmailId) => async (dispatch) => {
    await dispatch(duplicateAttachments(attachmentIds, bulkEmailId))
    await dispatch(patchBulkEmail())
}

/*
 **********************************************
 * Finalize Actions (sending/saving/deleting) *
 **********************************************
 */

// delete a draft that you are currently working on
export const deleteDraft = () => async (dispatch, getState) => {
    const { value } = await qual.fire(swalConfigs.OUTBOX_DELETE_DRAFT_ALERT_CONFIG)
    if (value) {
        const id = bulkEmailSelectors.getId(getState())
        dispatch(patchBulkEmail(id, { archived: true }))
        dispatch(segueToOutboxOverview())
        dispatch(resetBulkEmail(id))
    }
}

// Copy the current email to a new object in the DB
export const saveAsNewDraft = () => (dispatch, getState) => {
    const state = getState()
    const existingDraft = bulkEmailSelectors.selectPatchableFields(state)

    // Override some of the old drafts props to indicate it is a new draft
    const newDraft = {
        ...existingDraft,
        subject: `Copy of: ${existingDraft.subject}`,
        content_version: 1,
        is_draft: true,
        is_scheduled: false,
        current_status: DjangIO.app.userdata.outbox.types.EmailStatus.draft,
    }

    dispatch(createBulkEmail(newDraft)).then((response) => {
        // Segue to new URL
        dispatch(segueToBulkEmail(response.id))

        // we have to duplicate attachments if we have an email with attachments
        if (existingDraft.attachments.length > 0) {
            // duplicate the attachments and update
            dispatch(
                duplicateAttachmentsAndPatch(existingDraft.attachments.map((a) => Attachment.idFromResourceUri(a))),
            )
        }

        return response
    })
}

/**
 * This is the action creator called when the user clicks the Save Button
 *
 * @name saveBulkEmail
 * @function
 * @returns {Function} a thunk action creator
 */
export const saveBulkEmail = () => async (dispatch, getState) => {
    const state = getState()

    qual.fire(swalConfigs.OUTBOX_SAVING_IN_PROGRESS_ALERT)

    // save args
    const updateDict = {
        current_status: window.DjangIO.app.userdata.outbox.types.EmailStatus.draft.value,
        subject: bulkEmailSelectors.subject(state),
    }

    try {
        await dispatch(patchBulkEmail(undefined, updateDict, {}, "update_selection"))
        const { value: goToOverview } = await qual.fire(swalConfigs.OUTBOX_SAVE_SUCCESSFUL_ALERT)

        if (goToOverview) {
            dispatch(segueToOutboxOverview("draft"))
            dispatch(resetBulkEmail())
        }
    } catch (error) {
        BACKENDERROR(error)
    }
}

// Used by normal BulkEmails and ABTests when the subject is not being tested
export const validateSubject = (subject) => {
    if (!subject) {
        qual.fire(swalConfigs.MISSING_SUBJECT_ALERT)
        return false
    }

    return true
}

// Used when we're testing the subject
export const validateABTestSubjects = (variants) => {
    const variantsWithoutSubject = variants.reduce((badVariants, variant, index) => {
        const variantSubject = variant.get("subject")

        if (!variantSubject) {
            const variantIdentifier = String.fromCharCode(65 + index)
            return [...badVariants, variantIdentifier]
        }

        return badVariants
    }, [])

    if (variantsWithoutSubject.length) {
        qual.fire(swalConfigs.VARIANT_MISSING_SUBJECT_ALERT(variantsWithoutSubject))
        return false
    }

    return true
}

export const validateLinkTrackable = async (content, resetValidation) => {
    if (content.includes('href="')) {
        const links = content.split('href="').slice(1)
        // link could be placeholders
        const untrackedLinks = links
            .filter((link) => !/^(http|{{|mailto:)/.test(link))
            .map((link) => link.split('"')[0])

        if (untrackedLinks.length) {
            const { value: confirmSendMessage } = await qual.fire({
                title: "Warning",
                icon: "exclamation-triangle",
                iconFamily: "fas",
                text:
                    "Please check that the links in your email begin with http:// or https:// to ensure proper functionality and tracking.\n" +
                    `Untracked links:\n${untrackedLinks.join(",\n")}.`,
                actions: {
                    cancel: {
                        text: "Back to Editing",
                        value: false,
                    },
                    yes: {
                        text: "Ignore and Send",
                        value: true,
                    },
                },
            })

            if (!confirmSendMessage) {
                // turn off the validation
                resetValidation()
                return false
            }
        }
    }
    return true
}

/**
 * This method checks the email and will execute the
 * call back if the message is valid
 *
 * Called below in prepareSendTestEmail and confirmSend
 *
 * @name validateBulkEmail
 * @function
 * @param {Function} successCallback - callback send function (test or real send)
 */
export const validateBulkEmail = () => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)

    // do not execute if validating
    if (bulkEmailSelectors.getIsValidating(state)) {
        return false
    }

    // helper function
    const resetValidation = () => {
        dispatch(updateBulkEmail({ validating: false }, bulkEmailId))
        dispatch(resetPreviews())
    }

    const content = `${bulkEmailSelectors.subject(state)} ${bulkEmailSelectors.htmlContent(state)}`

    // check if the email content has links that are not trackable
    const hasTrackableLinks = await validateLinkTrackable(content, resetValidation)

    if (!hasTrackableLinks) {
        return false
    }

    // set state to validating
    dispatch(updateBulkEmail({ validating: true }, bulkEmailId))

    // show validating window
    qual.fire(swalConfigs.OUTBOX_VALIDATING_ALERT)

    // check if valid
    const error = bulkEmailSelectors.isMessageValid(state)

    if (error !== "") {
        qual.fire({
            title: "Message Invalid",
            text: error,
            actions: { ok: "OK" },
        })

        // turn off the validation
        resetValidation()
        return false
    }

    // make sure the message has placeholders before you validate
    if (!content.includes("{{")) {
        // mark valid
        resetValidation()
        return true
    }

    try {
        // update and validate placeholders
        const { valid, errors, is_format_error } = await dispatch(validatePlaceholders())
        // resolve
        resetValidation()

        // if not valid - sweetalert
        if (!valid) {
            qual.fire({
                title: "Warning",
                icon: "puzzle-piece",
                iconFamily: "fas",
                text: is_format_error
                    ? `Your message has the invalid placeholders:\n${errors.join(",\n")}`
                    : "Your message has invalid placeholders. Click preview to view them",
                actions: { ok: "OK" },
            })

            return false
        }

        return true
    } catch (e) {
        // reset the validation
        resetValidation()
        qual.close()
        // show error
        const message = e.response && e.response.data && e.response.data.message
        return BACKENDERROR(e, swalConfigs.outboxFailedEmailSend(message))
    }
}

/**
 * Called by confirmSend on each variant when scheduling an ABTest
 * Called by prepareSendTestEmail on each variant when sending an ABTest test email
 *
 * @name validateABTestVariant
 * @function
 * @param {number} [variantId] - The BulkEmail to validate
 * @returns {Promise<boolean>} - Whether the variant is valid, or rejects
 */
export const validateABTestVariant = (variantId) => async (dispatch, getState) => {
    const state = getState()

    // do not execute if validating
    if (bulkEmailSelectors.selectIsValidatingBulkEmail(variantId)(state)) {
        return Promise.reject("Already Validating")
    }

    // helper function
    const resetValidation = () => {
        dispatch(updateBulkEmail({ validating: false }, variantId))
        dispatch(resetPreviews(variantId))
    }

    const content = oneLine`
            ${bulkEmailSelectors.getABTestedSubject(variantId)(state)}
            ${bulkEmailSelectors.makeABTestBodyHTMLContent(variantId)(state)}
        `

    // check if email content has links that are not trackable
    const hasTrackableLinks = await validateLinkTrackable(content, resetValidation)
    if (!hasTrackableLinks) {
        return false
    }
    // set state to validating
    dispatch(updateBulkEmail({ validating: true }, variantId))

    // show validating window
    qual.fire(swalConfigs.OUTBOX_VALIDATING_ALERT)

    // check if valid
    const error = bulkEmailSelectors.selectIsMessageValid(variantId)(state)

    if (error !== "") {
        // turn off the validation
        resetValidation()
        return Promise.reject(error)
    }

    // make sure the message has placeholders before you validate
    if (!content.includes("{{")) {
        // mark valid
        resetValidation()
        return true
    }

    try {
        // update and validate placeholders
        const { valid, errors, is_format_error } = await dispatch(validatePlaceholdersForVariant(variantId))
        // resolve
        resetValidation()

        // if not valid - sweetalert
        if (!valid) {
            qual.fire({
                title: "Warning",
                icon: "puzzle-piece",
                iconFamily: "fas",
                text: is_format_error
                    ? `Your message has the invalid placeholders:\n${errors.join(",\n")}`
                    : "Your message has invalid placeholders. Click preview to view them",
                actions: { ok: "OK" },
            })

            return false
        }

        return true
    } catch (e) {
        // reset the validation
        resetValidation()
        qual.close()
        // show error
        const message = e.response && e.response.data && e.response.data.message
        return BACKENDERROR(e, swalConfigs.outboxFailedEmailSend(message))
    }
}

/**
 * @name validateABTest
 *
 * TODO: List of things being validated...
 *
 * @returns {boolean} isValid - Whether the current ABTest is valid
 */
export const validateABTest = () => async (dispatch, getState) => {
    const state = getState()

    const startTime = bulkEmailSelectors.selectABTestStartTime(state)
    const endTime = bulkEmailSelectors.selectABTestEndTime(state)

    /**
     * Step 1:
     * Validate the start and end times of the test
     */

    // Ensure both startTime and endTime are set
    if (!startTime || !endTime) {
        qual.fire(swalConfigs.SCHEDULED_WITHOUT_TIME)
        return false
    }

    // Ensure startTime is aftre ten minutes from now
    if (startTime.isBefore(moment().add(10, "minutes"))) {
        qual.fire(swalConfigs.SCHEDULED_START_TOO_SOON_ALERT)
        return false
    }
    // Ensure startTime is before endTime
    if (endTime.isBefore(startTime)) {
        qual.fire(swalConfigs.SCHEDULED_END_BEFORE_START_ALERT)
        return false
    }
    // Ensure endTime is at least 2 hours after startTime
    if (moment(startTime).add(2, "hours").isAfter(endTime)) {
        qual.fire(swalConfigs.SCHEDULED_END_TOO_SOON_ALERT)
        return false
    }

    /**
     * Step 2:
     * Validate Subject(s)
     */
    const testVariable = bulkEmailSelectors.selectABTestVariable(state)

    // If any variant has no subject - The test is not valid
    if (testVariable === ABTestVariable.subject.value) {
        const variants = bulkEmailSelectors.selectABTestVariants(state)
        if (!validateABTestSubjects(variants)) {
            return false
        }
        // Else, if the base BulkEmail has no subject - The test is not valid
    } else {
        const subject = bulkEmailSelectors.subject(state)
        if (!validateSubject(subject)) {
            return false
        }
    }

    /**
     * Step 3:
     * Validate message content, sender, placeholders, etc for each variant
     */
    const variantIds = bulkEmailSelectors.selectABTestVariantIds(state).toJS()
    const promises = []
    for (let i = 0; i < variantIds.length; i++) {
        promises.push(dispatch(validateABTestVariant(variantIds[i])))
    }

    try {
        const settledPromises = await Promise.all(promises)
        return settledPromises.every((promise) => promise)
    } catch (error) {
        qual.fire({
            title: "Message Invalid",
            text: error,
            actions: { ok: "OK" },
        })
        return false
    }
}

/**
 * This is the method called on send test email
 *
 * @name sendTestBulkEmail
 * @function
 * @param {String} testAddress - string of the person's email address
 */
export const sendTestBulkEmail = (testAddresses) => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)

    await dispatch(
        resourcePatchDetail(
            BulkEmail.objects.action("send_test_email", {
                email_address: JSON.stringify(testAddresses.replace(" ", "").split(",")),
            }),
            testingBulkEmailPieActions,
            bulkEmailId,
            {
                ...bulkEmailSelectors.selectPatchableFields(state),
                is_draft: true,
                current_status: window.DjangIO.app.userdata.outbox.types.EmailStatus.draft.value,
            },
            { bulkEmailId },
        ),
    )
}

/**
 * This is the method called on send test email when editing an ABTest
 *
 * @name sendTestABTestEmails
 * @function
 * @param {String} testAddress - string of the person's email address
 */
export const sendTestABTestEmails = (testAddresses) => async (dispatch, getState) => {
    const state = getState()
    const abTestId = bulkEmailSelectors.selectABTestId(state)

    // save the bulk email and variants so recent changes are included in test
    await dispatch(patchBulkEmail())

    await dispatch(
        resourcePatchAction(ABTest, testingBulkEmailPieActions, abTestId, {
            actionName: "send_test_emails",
            email_address: JSON.stringify(testAddresses.replace(" ", "").split(",")),
        }),
    )
}

/**
 * Generates a swal to confirm what email address will receive the test email(s).
 * For a standard BulkEmail, validates the BulkEmail message and then sends the
 * test email.
 * For an A/B test BulkEmail, validates the 2 variant BulkEmail
 * messages (not the base BulkEmail) and then sends the two variant test emails.
 *
 * This is the action creator called when the user clicks on Send Test Email
 *
 * @name prepareSendTestEmail
 * @function
 */
export const prepareSendTestEmail = () => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)
    const isABTest = bulkEmailSelectors.selectIsABTest(state)

    let inputValue

    try {
        const {
            value,
            state: { emails },
        } = await qual.fire(swalConfigs.OUTBOX_CONFIRM_TEST_EMAIL_ALERT)

        // Return early if they cancelled
        if (!value) {
            return
        }

        inputValue = emails
    } catch (e) {
        qual.close()
        return
    }

    if (inputValue === null) {
        // OUTBOX_CONFIRM_TEST_EMAIL_ALERT declares the alert not to close on submit,
        // so we need to manually close if we get bad data
        qual.close()
        return
    }

    // We check to see if the inputValue is an empty string and allow submission of
    // our default value. The only issue with this would be if the user deleted the default
    // text, the alert would still submit
    if (inputValue === "") {
        inputValue = window.Userdata.email
    }

    // checking that the email is valid
    const { hasValidAddresses, invalidAddresses } = parseTestEmailInput(inputValue)

    if (invalidAddresses.length) {
        const multipleInvalidAddresses = invalidAddresses.length > 1
        const title = multipleInvalidAddresses ? "Addresses" : "Address"
        const message = multipleInvalidAddresses ? "are" : "is"

        qual.fire({
            icon: "exclamation-circle",
            iconFamily: "fas",
            title: `Email ${title} Invalid`,
            text: `${invalidAddresses.join(", ")} ${message} invalid`,
            actions: { ok: "OK" },
        })

        return
    }

    if (!hasValidAddresses) {
        qual.fire({
            icon: "exclamation-circle",
            iconFamily: "fas",
            title: "Invalid Email Address",
            text: "You need to input a valid email address.",
            actions: { ok: "OK" },
        })

        return
    }

    // send if test message(s) pass validation
    let isValid = false
    if (isABTest) {
        const testVariable = bulkEmailSelectors.selectABTestVariable(state)
        if (testVariable === ABTestVariable.subject.value) {
            const variants = bulkEmailSelectors.selectABTestVariants(state)
            if (!validateABTestSubjects(variants)) {
                return
            }
        }
        // when sending test ABTest emails, validate each variant instead
        // of the base bulkemail
        const variantIds = bulkEmailSelectors.selectABTestVariantIds(state).toJS()
        const promises = []
        for (let i = 0; i < variantIds.length; i++) {
            promises.push(dispatch(validateABTestVariant(variantIds[i])))
        }
        try {
            const settledPromises = await Promise.all(promises)
            isValid = settledPromises.every((promise) => promise)
        } catch (error) {
            qual.fire({
                title: "Message Invalid",
                text: error,
                actions: { ok: "OK" },
            })
            isValid = false
        }
    } else {
        isValid = await dispatch(validateBulkEmail())
    }

    if (isValid) {
        try {
            isABTest ? await dispatch(sendTestABTestEmails(inputValue)) : await dispatch(sendTestBulkEmail(inputValue))

            qual.fire({
                icon: "check-circle",
                iconFamily: "fas",
                title: "Success",
                text: `A test email was sent to ${inputValue}`,
                actions: { ok: "OK" },
            })
        } catch (error) {
            // sendTestBulkEmail fires a patch request and thus can generate save conflicts
            if (error.response.status === 409) {
                swal(swalConfigs.OUTBOX_SAVE_CONFLICT_SWAL_CONFIG).then((value) => {
                    switch (value) {
                        case "overwrite":
                            swal(swalConfigs.OUTBOX_SAVE_CONFLICT_OVERWRITE_SWAL_CONFIG).then((confirm) => {
                                if (confirm) {
                                    // Update our copy with the latest content_version
                                    dispatch(
                                        updateBulkEmail(
                                            { content_version: error.response.data.content_version },
                                            bulkEmailId,
                                        ),
                                    )
                                    // Call the prepareSendTestEmail to attempt saving again
                                    dispatch(prepareSendTestEmail())
                                }
                            })
                            break

                        case "refresh":
                            swal(swalConfigs.OUTBOX_SAVE_CONFLICT_REFRESH_SWAL_CONFIG).then(
                                (confirm) => confirm && window.location.reload(),
                            )
                            break

                        case "save":
                            dispatch(saveAsNewDraft())
                            break

                        default:
                    }
                })
            } else {
                // We want to close the "...validating" modal if the user has been thrown a validation error
                // and the process has been terminated
                window.qual.close()
                const message =
                    (error.response && error.response.data && error.response.data.message) ||
                    (error.response && error.response.data)
                // sometimes the error response is a weird html string, we want to provide a default error message when that happens
                // https://quorumanalytics.atlassian.net/browse/CRM-3815
                BACKENDERROR(
                    error,
                    swalConfigs.outboxBadTestEmail(
                        message.indexOf("<html") > -1
                            ? "Please check your internet connection and email settings, and try again. If the issue persists, contact support for assistance."
                            : message,
                    ),
                )
            }
        }
    }
}

/**
 * @name recordUserActions
 *
 * Fires off UserActions recording the number of emails sent; total and to each dataType
 *
 * @param {Number} numEmails Total number of recipients who will receive the email
 * @param {Number} numPerson Total number of Persons who will receive the email
 * @param {Number} numSupporters Total number of Supporters who will receive the email
 * @returns {Promise}
 */
export const recordUserActions = (numEmails, numPerson, numSupporters) => (dispatch) => {
    if (numPerson) {
        dispatch(
            sendUserAction(
                DjangIO.app.useractions.types.UserActionType.outbox_emails_officials_staff,
                `${numPerson} Emails Sent`,
            ),
        )
    }

    if (numSupporters) {
        dispatch(
            sendUserAction(
                DjangIO.app.useractions.types.UserActionType.outbox_emails_contacts,
                `${numSupporters} Emails Sent`,
            ),
        )
    }

    return dispatch(sendUserAction(DjangIO.app.useractions.types.UserActionType.send_email, `${numEmails} Emails Sent`))
}

/**
 * This action creator is called by sendIfValid and confirmSend
 *
 * @name sendBulkEmail
 * @function
 * @returns {Promise} promise of the message
 */
export const sendBulkEmail = () => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)
    const isScheduled = bulkEmailSelectors.isScheduled(state)

    // text for success
    const inProgressHeader = isScheduled ? "Scheduling..." : "Sending..."
    const inProgressText = isScheduled ? "Your message is being scheduled." : "Your message is sending."
    const successText = isScheduled ? "Your email has been scheduled!" : "Your email sent!"

    qual.fire({
        title: inProgressHeader,
        text: inProgressText,
        closeOnClickOutside: false,
    })

    // function called on send success
    const onSendSuccess = () => {
        qual.fire({
            icon: "check-circle",
            iconFamily: "fas",
            title: "Success",
            text: successText,
        })

        setTimeout(() => {
            // First segue to the Overview page, so we don't render a sent/scheduled email in the compose form
            dispatch(segueToOutboxOverview())

            // Now reset the store, so that opening another draft doesn't segue to the details page before it can load the right email
            dispatch(resetBulkEmail())

            // fake a success
            qual.close()

            // if it hasn't been dispatched yet, redirect
            if (bulkEmailSelectors.isSending(state)) {
                dispatch({
                    type: sendingBulkEmailPieActions.success,
                    bulkEmailId,
                })
            }
        }, SEND_REDIRECT_TIMER)
    }

    try {
        await dispatch(
            resourcePatchDetail(
                BulkEmail.objects.action("send_message"),
                sendingBulkEmailPieActions,
                bulkEmailId,
                {
                    ...bulkEmailSelectors.selectPatchableFields(state),
                    scheduled_send_time: bulkEmailSelectors.isScheduled(state)
                        ? moment(bulkEmailSelectors.scheduledSendTime(state)).tz("America/New_York")
                        : moment().format(),
                    current_status: window.DjangIO.app.userdata.outbox.types.EmailStatus.injecting.value,
                },
                { bulkEmailId },
            ),
        )

        onSendSuccess()
    } catch (error) {
        if (error.response.status === 409) {
            swal(swalConfigs.OUTBOX_SAVE_CONFLICT_SWAL_CONFIG).then((value) => {
                switch (value) {
                    case "overwrite":
                        swal(swalConfigs.OUTBOX_SAVE_CONFLICT_OVERWRITE_SWAL_CONFIG).then((confirm) => {
                            if (confirm) {
                                // Update our copy with the latest content_version
                                dispatch(
                                    updateBulkEmail(
                                        { content_version: error.response.data.content_version },
                                        bulkEmailId,
                                    ),
                                )

                                // Call sendBulkEmail to attempt saving again
                                dispatch(sendBulkEmail())
                            }
                        })
                        break

                    case "refresh":
                        swal(swalConfigs.OUTBOX_SAVE_CONFLICT_REFRESH_SWAL_CONFIG).then(
                            (confirm) => confirm && window.location.reload(),
                        )
                        break

                    case "save":
                        dispatch(saveAsNewDraft())
                        break

                    default:
                }
            })
        } else {
            window.qual.close()
            const message = error.response && error.response.data && error.response.data.message
            BACKENDERROR(error, swalConfigs.outboxFailedEmailSend(message))
        }
    }
}

/**
 * This action creator is called by confirmSend
 *
 * @name scheduleABTest
 * @function
 * @returns {Promise} promise of the message
 */
export const scheduleABTest = () => async (dispatch, getState) => {
    const state = getState()
    const abTestId = bulkEmailSelectors.selectABTestId(state)

    qual.fire({
        title: "Scheduling...",
        text: "Your test is being scheduled.",
        closeOnClickOutside: false,
    })

    // function called on send success
    const onSendSuccess = () => {
        qual.fire({
            icon: "check-circle",
            iconFamily: "fas",
            title: "Success",
            text: "Your test has been scheduled!",
        })

        setTimeout(() => {
            // First segue to the Overview page, so we don't render a sent/scheduled test in the compose form
            dispatch(segueToOutboxOverview())

            // Now reset the store, so that opening another draft doesn't segue to the details page before it can load the right email
            dispatch(resetBulkEmail())

            qual.close()
        }, SEND_REDIRECT_TIMER)
    }

    try {
        // Patch all of our emails to make sure the latest changes are reflected
        await dispatch(patchBulkEmail())

        // Schedule the test!
        await dispatch(
            resourcePatchAction(ABTest, sendingBulkEmailPieActions, abTestId, { actionName: "schedule_abtest" }),
        )

        onSendSuccess()
    } catch (error) {
        window.qual.close()
        const message = error.response && error.response.data && error.response.data.message
        BACKENDERROR(error, swalConfigs.outboxFailedEmailSend(message))
    }
}

/**
 * Actually dispatches the sendBulkEmail action after validation
 * Called by confirmSend
 *
 * @name sendIfValid
 * @function
 * @param {Number} numEmails Total number of recipients who will receive the email
 * @param {Number} numPerson Total number of Persons who will receive the email
 * @param {Number} numSupporters Total number of Supporters who will receive the email
 * @returns {Function} Function that dispatches the send
 */
export const sendIfValid = (numEmails, numPerson, numSupporters) => async (dispatch) => {
    // check if it is valid
    try {
        const isValid = await dispatch(validateBulkEmail())

        // if it is valid and below the limit then send!
        if (isValid) {
            await dispatch(sendBulkEmail())
            dispatch(recordUserActions(numEmails, numPerson, numSupporters))
        }
    } catch (e) {
        BACKENDERROR(e)
    }
}

/**
 * confirmSend
 *  - function run when the user wants to send the email - it validates and the
 *    executes the send
 *
 * This is the function actually called when the user clicks Send/Schedule
 *
 * @name confirmSend
 * @function
 */
export const confirmSend = () => async (dispatch, getState) => {
    const state = getState()
    const isScheduled = bulkEmailSelectors.isScheduled(state)
    const isABTest = bulkEmailSelectors.selectIsABTest(state)

    let isValid = false

    // check if Issues are associated if it is required in org setting
    const hasValidProjects = dispatch(hasValidateBulkEmailProject())
    if (!hasValidProjects) {
        qual.fire(swalConfigs.OUTBOX_PROJECT_VALIDATION_FAIL_ALERT)
        return
    }

    if (isABTest) {
        isValid = await dispatch(validateABTest())
    } else {
        const scheduledSendTime = bulkEmailSelectors.scheduledSendMoment(state)

        // prevent emails scheduled before the current time from sending.
        if (isScheduled && scheduledSendTime.isBefore(moment().subtract(1, "minutes"))) {
            qual.fire(swalConfigs.SCHEDULED_BEFORE_NOW_ALERT)
            return
        }

        // Check for missing subject and whether to send anyways
        const subject = bulkEmailSelectors.getSubject(state)
        if (!validateSubject(subject)) {
            return
        }

        // Validate message content, sender, placeholders, etc
        isValid = await dispatch(validateBulkEmail())
    }

    if (!isValid) {
        return
    }

    // Done validating, now get final confirmation before Sending/Scheduling!
    let title
    const abTestVariable = bulkEmailSelectors.selectABTestVariable(state)
    const numEmails = bulkEmailSelectors.emailCount(state)
    const rcptName = `Recipient${numEmails > 1 || numEmails === 0 ? "s" : ""}`
    const fromName = bulkEmailSelectors.selectFromNameInSwal(state)

    if (isABTest) {
        title =
            abTestVariable === DjangIO.app.userdata.outbox.types.ABTestVariable.from_address.value
                ? `Schedule A/B Test to ${numEmails.toLocaleString()} ${rcptName}?`
                : `Schedule A/B Test to ${numEmails.toLocaleString()} ${rcptName} ${fromName}?`
    } else if (isScheduled) {
        title = `Schedule Email to ${numEmails.toLocaleString()} ${rcptName} ${fromName}?`
    } else {
        title = `Send Email to ${numEmails.toLocaleString()} ${rcptName} ${fromName}?`
    }

    // Render confirmation alert
    const { value: confirmSendMessage } = await qual.fire({
        icon: "paper-plane",
        iconFamily: "fas",
        title,
        children: isABTest ? <ABSendConfirmation /> : <SendConfirmation />,
        actions: {
            cancel: true,
            yes: {
                text: isABTest || isScheduled ? "Schedule" : "Send",
                value: true,
            },
        },
    })

    if (confirmSendMessage) {
        isABTest ? await dispatch(scheduleABTest()) : await dispatch(sendBulkEmail())
    }
}

/**
 ************************************************
 * Action Creators for initializing a BulkEmail *
 * with a pre-selected selection of recipients. *
 *                                              *
 * ex: Resending old email, people in a list,   *
 * participated in campaigns, etc               *
 * Sending a standard BulkEmail based on a     *
 * completed standard or AB Test BulkEmail *
 ************************************************
 */

/**
 * Action Creator that triggers the regenerating of an email
 *
 * @param {object} regenType object containing the regen info
 * @returns {Promise} return the creation promise
 */
export const regenerateBulkEmail = (regenType) => async (dispatch, getState) => {
    qual.fire({
        title: "Generating Email",
        text: "Just a moment...",
        icon: "info",
        buttons: {},
        closeOnClickOutside: false,
    })

    // the current dict
    const state = getState()
    const oldId = bulkEmailSelectors.getId(state)

    // dict of current email
    const currentEmail = bulkEmailSelectors.selectPatchableFields(state)

    // when the current email is an AB Test base BulkEmail, we want to regenerate a standard email from:
    //      for Send with Winner/send_best mode, the sent Winner email's fields
    //      for Send without Winner/learning mode, the best Variant email's fields
    const isABTest = bulkEmailSelectors.selectIsABTest(state)
    const isABTestModeLearning =
        bulkEmailSelectors.selectABTestMode(state) === DjangIO.app.userdata.outbox.types.ABTestMode.learning.value

    const winnerEmailId = bulkEmailSelectors.selectABTestWinnerId(state)
    const winnerEmail = bulkEmailSelectors.selectVariantPatchableFields(winnerEmailId)(state)
    const bestVariantEmailId = bulkEmailSelectors.selectABTestBestVariantId(state)
    const bestVariantEmail = bulkEmailSelectors.selectVariantPatchableFields(bestVariantEmailId)(state)

    const currentBestVariantOrWinnerEmail = isABTestModeLearning ? bestVariantEmail : winnerEmail

    const advSearchFilters = { [regenType.api_tag]: oldId }

    // Make the selection
    const selection = []

    if (regenType.api_tag !== "new_recipient_list") {
        if (bulkEmailSelectors.selectHasOfficials(state)) {
            const personTypes = DjangIO.app.person.types.PersonType.items()
                .filter((type) =>
                    // In the EU, staffers are considered officials...
                    Userdata.isOnlyEUMode()
                        ? // so either return all types (Except Press Contacts) if isOnlyEUMode
                          type.value !== DjangIO.app.person.types.PersonType.press_contact.value
                        : // or return types where is_official is True
                          type.is_official,
                )
                .map((type) => type.value)

            selection.push({
                id: `${regenType.api_tag} official`,
                advSearchParams: [
                    {
                        ...advSearchFilters,
                        most_recent_person_type__in: personTypes,
                    },
                ],
                position: 100,
                quorumDataType: DjangIO.app.models.QuorumDataType.person.value,
                quorumObjType: "other",
                styling: DjangIO.app.models.QuorumDataType.person.outbox_selection_styling,
                text: regenType.verbage.official,
            })
        }
        if (bulkEmailSelectors.selectHasStaffers(state)) {
            selection.push({
                id: `${regenType.api_tag} staffer`,
                advSearchParams: [
                    {
                        ...advSearchFilters,
                        most_recent_person_type__in: [DjangIO.app.person.types.PersonType.staffer.value],
                    },
                ],
                position: 100,
                quorumDataType: DjangIO.app.models.QuorumDataType.staffer.value,
                quorumObjType: "other",
                styling: DjangIO.app.models.QuorumDataType.staffer.outbox_selection_styling,
                text: regenType.verbage.staffer,
            })
        }
        if (bulkEmailSelectors.selectHasPressContacts(state)) {
            selection.push({
                id: `${regenType.api_tag} press`,
                advSearchParams: [
                    {
                        ...advSearchFilters,
                        most_recent_person_type__in: [DjangIO.app.person.types.PersonType.press_contact.value],
                    },
                ],
                position: 100,
                quorumDataType: DjangIO.app.models.QuorumDataType.press_contact.value,
                quorumObjType: "other",
                styling: DjangIO.app.models.QuorumDataType.press_contact.outbox_selection_styling,
                text: regenType.verbage.press_contact,
            })
        }
        if (bulkEmailSelectors.supporterCount(state) > 0) {
            selection.push({
                id: `${regenType.api_tag} supporter`,
                advSearchParams: [advSearchFilters],
                position: 100,
                quorumDataType: DjangIO.app.models.QuorumDataType.supporter.value,
                quorumObjType: "other",
                styling: DjangIO.app.models.QuorumDataType.supporter.outbox_selection_styling,
                text: regenType.verbage.supporter,
            })
        }
    }

    // dispatch the regeneration event
    const response = await dispatch(
        createBulkEmail({
            ...currentEmail,
            ...(isABTest && currentBestVariantOrWinnerEmail),
            // overwritten
            ...(regenType.api_tag === "new_recipient_list" && { excludes_json: { data: [] } }),
            abtest: null,
            attachments: [],
            current_status: DjangIO.app.userdata.outbox.types.EmailStatus.draft.value,
            is_draft: true,
            is_scheduled: false,
            projects: [],
            scheduled_send_time: moment().format(),
            selection_json: { data: selection },
            transmission_json: {},
            user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
        }),
    )

    if (isABTest) {
        // we have to duplicate attachments if we have an email with attachments
        if (currentBestVariantOrWinnerEmail.attachments.length > 0) {
            // duplicate the attachments and update
            await dispatch(
                duplicateAttachmentsAndPatch(
                    currentBestVariantOrWinnerEmail.attachments.map((a) => Attachment.idFromResourceUri(a)),
                    response.id,
                ),
            )
        }
        // we have to duplicate attachments if we have an email with attachments
    } else if (currentEmail.attachments.length > 0) {
        // duplicate the attachments and update
        await dispatch(
            duplicateAttachmentsAndPatch(
                currentEmail.attachments.map((a) => Attachment.idFromResourceUri(a)),
                response.id,
            ),
        )
    }

    // change pages
    dispatch(segueToBulkEmail(response.id))

    qual.close()

    return response
}

/**
 * Action Creator that handles when there are URL query params
 * Used when resending an old email to the same recipients
 * TODO: is_abtest may also be in these query params. Should we handle here?
 *
 * @param {object} query
 * @param {String} query.toPeople - json array of people ids
 * @param {String} query.toSupporters - json array of supporters
 * @returns {Promise<object>[]} - array of promises
 */
export const loadQueryParams =
    ({ toPeople, toSupporters }) =>
    (dispatch, getState) => {
        /**
         * helper function called on load, it updates the store
         *
         * @name onLoad
         * @function
         * @param {object} - Response object to load into the store
         * @param {object} response - response object that has the list of rcpt tags
         */
        const onLoad = (response) =>
            response.objects
                ? dispatch(
                      updateSelection({
                          selection: bulkEmailSelectors.selection(getState()).concat(response.objects),
                      }),
                  )
                : Promise.resolve(response)

        // the array of queries
        const queries = []

        // parse url people
        if (toPeople) {
            const personIds = JSON.parse(toPeople)
            queries.push(dispatch(loadPeople(personIds)).then(onLoad))
        }
        if (toSupporters) {
            const supporterIds = JSON.parse(toSupporters)
            queries.push(dispatch(loadSupporters(supporterIds)).then(onLoad))
        }

        return queries
    }

/**
 * ActionCreator that takes a list and adds it to the outbox selection
 *
 * @name addListAsSelection
 * @function
 * @param {object} list - a list object with filters, list type and a name
 */
export const addListAsSelection = (list) => (dispatch, getState) =>
    dispatch(
        updateSelection({
            selection: bulkEmailSelectors.selection(getState()).concat([
                {
                    advSearchParams: [list.filters],
                    id: `${JSON.stringify(list.filters)}type-1`,
                    quorumDataType: list.list_type,
                    selectionType: "selection",
                    text: list.name,
                },
            ]),
        }),
    )

/**
 * ActionCreator that takes a campaign and adds it to the outbox selection
 *
 * @name addCampaignAsSelection
 * @function
 * @param {String|Number} campaignId
 * @param {String} campaignName
 */
export const addCampaignAsSelection = (campaignId, campaignName) => (dispatch, getState) => {
    const advSearchFilters = [{ involved_in_campaigns: campaignId }]
    return dispatch(
        updateSelection({
            selection: bulkEmailSelectors.selection(getState()).concat([
                {
                    advSearchParams: advSearchFilters,
                    id: `for_campaign_${campaignId}`,
                    quorumDataType: DjangIO.app.models.QuorumDataType.supporter.value,
                    selectionType: "selection",
                    text: `Relevant Contacts for ${campaignName}`,
                    styling: DjangIO.app.models.QuorumDataType.supporter.outbox_selection_styling,
                },
            ]),
        }),
    )
}

/**
 * ActionCreator that will let us know that this user came from the campaign "Email" button
 */
export const setIsFromCampaign = (bulkEmailId) => (dispatch) =>
    dispatch({
        type: bulkEmailActions.SET_IS_FROM_CAMPAIGN,
        bulkEmailId,
    })

/**
 * ActionCreator that gets the proper querymethod to use for generating a selection
 * for an event email
 *
 * Only used in addCustomEventParticipants below
 *
 * @name getCustomEventFilter
 * @function
 * @param {object} params
 * @param {String|Number} params.eventId
 * @param {Boolean} params.isNotReminder
 *      querymethod because it is on the same model as officials
 * @param {Boolean} params.attended
 * @param {Boolean} params.notInvited
 * @param {Number} params.rsvpValue - RSVPStatus value
 * @param {Boolean} [params.isStaffer=false] - for staffer querymethods we need to call a separate
 * @returns {{[String]: String|Number}}
 */
export const getCustomEventFilter = ({
    eventId,
    isNotReminder,
    attended,
    notInvited,
    rsvpValue,
    isStaffer = false,
}) => {
    const stafferPrefix = isStaffer ? "staffers_" : ""

    if (isNotReminder) {
        return { [`${stafferPrefix}involved_in_event`]: eventId }
    }

    if (attended) {
        return { [`${stafferPrefix}attended_event`]: eventId }
    }

    if (notInvited) {
        return { [`${stafferPrefix}has_not_been_emailed`]: eventId }
    }

    const { yes, no, maybe } = DjangIO.app.custom_event.types.RSVPStatus

    switch (rsvpValue) {
        case yes.value:
            return { [`${stafferPrefix}has_rsvped_yes`]: eventId }
        case no.value:
            return { [`${stafferPrefix}has_rsvped_no`]: eventId }
        case maybe.value:
            return { [`${stafferPrefix}has_rsvped_maybe`]: eventId }
        default:
            return { [`${stafferPrefix}has_not_rsvped`]: eventId }
    }
}

/**
 * Only used in addCustomEventParticipants below
 *
 * @param {object} params
 * @param {"capitol"|"district"|"both"} params.schedulerType
 * @param {String|Number} params.eventId
 * @returns {object[]}
 */
export const getSchedulerFilter = ({ schedulerType, eventId }) => [
    {
        members: {
            querysetList: [{ involved_in_event: eventId }],
            added_ids: [],
            deleted_ids: [],
        },
        staffer_titles: {
            // TODO: YIKES
            // the id for the scheduler role_type
            addedIds: [11513],
            deletedIds: [],
            querysetList: [],
        },
        ...SCHEDULER_TYPES[schedulerType].scheduler_filter,
    },
]

/**
 * ActionCreator that takes the participants of a custom event  and adds them
 to the outbox selection
 *
 * @name addCustomEventParticipants
 * @function
 * @param {object} params
 * @param {String|Number} params.eventId
 * @param {String} params.eventName
 * @param {Number} params.rsvpValue - RSVPStatus value
 * @param {Boolean} params.isNotReminder
 * @param {Boolean} params.attended
 * @param {Boolean} params.notInvited
 * @param {Boolean} params.addContactSelection
 * @param {Boolean} params.addOfficialSelection
 * @param {Boolean} params.addStafferSelection
 * @param {Boolean} params.addSchedulers
 * @param {"capitol"|"district"|"both"} params.schedulerType
 */
export const addCustomEventParticipants =
    ({
        eventId,
        eventName,
        rsvpValue,
        isNotReminder,
        attended,
        notInvited,

        addContactSelection,
        addOfficialSelection,
        addStafferSelection,
        addSchedulers,

        schedulerType,
    }) =>
    (dispatch, getState) => {
        const advSearchParams = [
            getCustomEventFilter({
                eventId,
                rsvpValue,
                isNotReminder,
                attended,
                notInvited,
            }),
        ]

        const stafferAdvSearchParams = [
            getCustomEventFilter({
                eventId,
                rsvpValue,
                isNotReminder,
                attended,
                notInvited,
                isStaffer: true,
            }),
        ]

        return dispatch(
            updateSelection({
                selection: bulkEmailSelectors.selection(getState()).concat(
                    [
                        // official selection
                        !addSchedulers &&
                            addOfficialSelection && {
                                advSearchParams,
                                id: `officials_involved_in_event_${eventId}`,
                                quorumDataType: DjangIO.app.models.QuorumDataType.person.value,
                                selectionType: "selection",
                                text: `Relevant Officials for ${eventName}`,
                                styling: DjangIO.app.models.QuorumDataType.person.outbox_selection_styling,
                            },
                        // staffer selection
                        !addSchedulers &&
                            addStafferSelection && {
                                advSearchParams: stafferAdvSearchParams,
                                id: `staffers_involved_in_event_${eventId}`,
                                quorumDataType: DjangIO.app.models.QuorumDataType.staffer.value,
                                selectionType: "selection",
                                text: `Relevant ${DjangIO.app.models.QuorumDataType.staffer.plural} for ${eventName}`,
                                styling: DjangIO.app.models.QuorumDataType.staffer.outbox_selection_styling,
                            },
                        // supporter selection
                        !addSchedulers &&
                            addContactSelection && {
                                advSearchParams,
                                id: `supporters_involved_in_event_${eventId}`,
                                quorumDataType: DjangIO.app.models.QuorumDataType.supporter.value,
                                selectionType: "selection",
                                text: `Relevant Contacts for ${eventName}`,
                                styling: DjangIO.app.models.QuorumDataType.supporter.outbox_selection_styling,
                            },
                        addSchedulers && {
                            advSearchParams: getSchedulerFilter({ schedulerType, eventId }),
                            id: `schedulers_for_officials_involved_in_event_${eventId}`,
                            quorumDataType: DjangIO.app.models.QuorumDataType.staffer.value,
                            selectionType: "selection",
                            text: `${SCHEDULER_TYPES[schedulerType].selectionLabel} Schedulers of Officials for ${eventName}`,
                            styling: DjangIO.app.models.QuorumDataType.staffer.outbox_selection_styling,
                        },
                    ].filter((item) => !!item),
                ),
            }),
        )
    }

/**
 * ActionCreator that takes the relevant emails from rows of an official / staffer / supporter
 * sheet and adds them to the outbox selection
 *
 * @name addPeopleFromSheet
 * @function
 * @param {object} params
 * @param {String} params.sheetId - the uid of the sheet
 * @param {String} params.sheetName - the name of the sheet
 * @param {object} params.advSearchParams - the assoceated SafedSearch
 * @param {String[]} params.added_ids - ids to add not included in SafedSearch
 * @param {String[]} params.deleted_ids - ids to remove not included in SafedSearch
 * @param {String[]} params.created_regions - regions the sheet was created in
 * @param {Boolean} params.addContactSelection - if we should add a contact selection
 * @param {Boolean} params.addOfficialSelection - if we should add a official selection
 * @param {Boolean} params.addStafferSelection - if we should add a staffer selection
 * @param {Number} params.sheetDataType QuorumDataType
 */
export const addPeopleFromSheet =
    ({
        sheetId,
        sheetName,
        advSearchParams,
        added_ids,
        deleted_ids,
        created_regions,

        addContactSelection,
        addOfficialSelection,
        addStafferSelection,
        sheetDataType,
    }) =>
    (dispatch, getState) => {
        const wrappedFilters = wrapFiltersInForSearchify(advSearchParams, sheetDataType)
        // take the intersection of the user's current regions and the regions the sheet was created in
        const region_intersection = Userdata.current_regions_values.filter((reg) => created_regions.includes(reg))
        // if the two sets of regions are distinct, default value is the region sheet was created in which keeps the recipients tied to the sheet rows
        var forced_regions = region_intersection.length ? region_intersection : created_regions

        return dispatch(
            updateSelection({
                selection: bulkEmailSelectors.selection(getState()).concat(
                    [
                        // official selection
                        addOfficialSelection && {
                            advSearchParams: wrappedFilters,
                            added_ids,
                            deleted_ids,
                            id: `officials_from_sheet_${sheetId}`,
                            quorumDataType: DjangIO.app.models.QuorumDataType.person.value,
                            selectionType: "selection",
                            text: `Relevant ${DjangIO.app.models.QuorumDataType.person.plural} from sheet: ${sheetName}`,
                            styling: DjangIO.app.models.QuorumDataType.person.outbox_selection_styling,
                            always_forced_regions: forced_regions,
                        },
                        // staffer selection
                        addStafferSelection && {
                            advSearchParams: wrappedFilters,
                            added_ids,
                            deleted_ids,
                            id: `staffers_from_sheet_${sheetId}`,
                            quorumDataType: DjangIO.app.models.QuorumDataType.staffer.value,
                            selectionType: "selection",
                            text: `Relevant ${DjangIO.app.models.QuorumDataType.staffer.plural} from sheet: ${sheetName}`,
                            styling: DjangIO.app.models.QuorumDataType.staffer.outbox_selection_styling,
                            always_forced_regions: forced_regions,
                        },
                        // supporter selection
                        addContactSelection && {
                            advSearchParams: wrappedFilters,
                            added_ids,
                            deleted_ids,
                            id: `supporters_from_sheet_${sheetId}`,
                            quorumDataType: DjangIO.app.models.QuorumDataType.supporter.value,
                            selectionType: "selection",
                            text: `Relevant ${DjangIO.app.models.QuorumDataType.supporter.plural} from sheet: ${sheetName}`,
                            styling: DjangIO.app.models.QuorumDataType.supporter.outbox_selection_styling,
                            always_forced_regions: forced_regions,
                        },
                    ].filter((item) => !!item),
                ),
            }),
        )
    }

/**
 ****************************
 * Template Action Creators *
 ****************************
 */

/**
 * Helper function that updates the beefree instance when a template is loaded
 *
 * @name loadHelper
 * @function
 * @param {object} newJson - the BeeFree json
 * @param {object} state - the state of the store
 */
const loadHelper = (newJson, state, bulkEmailId) => {
    if (bulkEmailSelectors.selectVariantComposeTool(bulkEmailId)(state) === "beefree") {
        const beeFreeInstance = bulkEmailSelectors.selectBeePlugin(bulkEmailId)(state)
        if (beeFreeInstance && beeFreeInstance.load) {
            beeFreeInstance.load(newJson)
        }
    }
}

/**
 * Action creator that takes in a template object and loads it into the store
 *
 * @name loadTemplate
 * @function
 * @param {object} template - object containing all of the template attributes
 */
export const loadTemplate = (template, variantId) => (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = variantId || bulkEmailSelectors.getId(state)

    /**
     * Helper function that loads the template
     *
     * @name onLoad
     * @function
     * @param {object} newJson - the json object
     * @param {String} newHtml - the html string
     */
    const onLoad = (newJson, newHtml) => {
        // if we don't have a default template then we can just
        // update the email
        dispatch(
            updateBulkEmail(
                {
                    json_content: newJson || {},
                    html_content: newHtml || "",
                    attachments: [],
                    subject: template.name || "",
                },
                bulkEmailId,
            ),
        )

        // Load the BeeFree editor if needed
        loadHelper(newJson, state, bulkEmailId)

        // we want to reset the previews
        dispatch(resetPreviews())

        // if there are attachments, then we need to duplicate them!
        if (template.attachments && template.attachments.length > 0) {
            dispatch(duplicateAttachments(template.attachments.map((a) => Attachment.idFromResourceUri(a))))
        }
    }

    // get all the placeholders for the user for a beeFree template
    dispatch(updatePlaceholders(bulkEmailId, { is_template: true }))

    // if the template is the default then we have to get it.
    // otherwise, just call the load function
    if (template.default) {
        // eslint-disable-next-line no-undef
        $.getJSON(generateStaticUrl(`outbox-templates/${template.slug}.json`), (templateJson) =>
            onLoad(templateJson, template.slug),
        )
    } else {
        onLoad(template.json_content, template.html_content)
    }
}

/**
 * @param {object} params
 * @param {object} params.searchifyFilters
 */
export const submitSearchifyFromOutbox =
    ({ searchifyFilters }) =>
    async (dispatch, getState) => {
        const state = getState()

        let initialOutboxSearch = state.outbox.get("initialOutboxSearch")

        if (initialOutboxSearch.toJS) {
            initialOutboxSearch = initialOutboxSearch.toJS()
        }

        // init new selection obj
        const newCustomObj = {
            id: initialOutboxSearch.id || makeId(30),
            quorumDataType: initialOutboxSearch.quorumDataType,
            advSearchParams: searchifyFilters.querysetList,
            added_ids: searchifyFilters.addedIds,
            deleted_ids: searchifyFilters.deletedIds,
            selectionType: initialOutboxSearch.selectionType,
        }

        // try to make the new selection
        let errorString = ""

        // also make new selection object
        const curSelection = bulkEmailSelectors[initialOutboxSearch.selectionType](state)

        // presumably this is for contacts that are generated from lists.
        const cleanSelection = curSelection.filter((obj) => {
            // this excludes the old item, if it existed
            if (obj.id === initialOutboxSearch.id && obj.text === initialOutboxSearch.text) {
                return false
            }

            if (obj.id === newCustomObj.id && obj.quorumDataType === newCustomObj.quorumDataType) {
                // no 2 same selections
                errorString = "This selection already exists"
                errorString += ` as list: "${obj.text}"`
                return false
            }
            return true
        })

        // once we have the cleaned selection, process it
        if (errorString !== "") {
            // if there was a dup error, we need re launch adv search
            swal("Error", errorString, "error")
        }

        // change text for input
        let placeholderText = ""

        if (initialOutboxSearch.id === null) {
            placeholderText = "Add a title to remember your selection"
        } else {
            placeholderText = "Change the title of your selection"
        }

        // sweet alert to name selection
        const inputValue = await swal({
            title: placeholderText,
            content: {
                element: "input",
                attributes: {
                    placeholder: initialOutboxSearch.text,
                    value: initialOutboxSearch.text,
                },
            },
            buttons: {
                cancel: true,
                confirm: true,
            },
            closeOnClickOutside: false,
        })

        // if they hit cancel, do nothing.
        if (inputValue === null) {
            return
        }

        // set the name of the selection
        newCustomObj.text = inputValue || initialOutboxSearch.text

        // if the name is duplicated, alert user - otherwise update
        if (cleanSelection.some((obj) => obj.text === newCustomObj.text)) {
            swal("A selection with this name already exists", "", "error")
            return
        }

        // fire the submit action (searchify_submit is in outbox reducer, despite
        // the searchActionTypes. Don't get fooled!)
        dispatch({ type: searchifyActionTypes.SEARCHIFY_SUBMIT })

        // update the selection_json to add our new object!
        dispatch(
            updateSelection({
                [initialOutboxSearch.selectionType]: cleanSelection.concat([newCustomObj]),
            }),
        )
    }

/**
 * archive a draft in the outbox table
 * @param {object} param
 * @param {String|Number} param.datumId
 */
export const archiveDraft = ({ datumId }) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.userdata.outbox.models.bulk_email.BulkEmail,
        patchBulkEmailPieActions,
        datumId,
        {
            archived: true,
            bcc_rcpts: [],
            cc_rcpts: [],
            merge_variables: [],
        },
        { datumId },
    )

/**
 **********************************
 * Action Creators for the        *
 * BulkEmail Profile/Details page *
 **********************************
 */

/**
 * Load the BulkEmail, but also check to ensure it is not a draft before showing in Details page
 *
 * @param {String} id - The database ID of the BulkEmail
 * @returns {Promise<object>} returns the request promise
 */
export const loadBulkEmailForProfile = (id) => async (dispatch) => {
    dispatch(setActiveBulkEmail(id))
    const bulkEmail = await dispatch(loadBulkEmail(id))
    if (bulkEmail && bulkEmail.id) {
        if (bulkEmail.is_draft) {
            // never show the details page for a draft
            // go to compose form
            if (bulkEmail.root_bulk_email) {
                // for variants and winners of AB tests, go to the root email's form
                dispatch(segueToBulkEmail(BulkEmail.idFromResourceUri(bulkEmail.root_bulk_email)))
            } else {
                dispatch(segueToBulkEmail(bulkEmail.id))
            }
        } else if (bulkEmail.root_bulk_email) {
            // if this is part of an ABTest (a variant or winner)
            // we should only ever show the root bulk email
            dispatch(segueToEmailDetail(BulkEmail.idFromResourceUri(bulkEmail.root_bulk_email)))
        } else if (bulkEmail.is_abtest) {
            // If this is an ABTest, we need to get the variants for the profile, too
            dispatch(loadABTestVariants(bulkEmail.id))
        }
    }
}

/**
 * Action creator that cancels a scheduled email
 *
 * @param {object} params
 * @param {String} params.id - string of dbID
 * @param {moment} params.scheduledTime - Moment object representing the time the email is scheduled to be sent
 * @returns {Promise<boolean>} promise from the cancel
 */
export const cancelBulkEmailWithSwal =
    ({ id, scheduledTime }) =>
    async (dispatch, getState) => {
        const state = getState()
        const bulkEmailId = id || bulkEmailSelectors.getId(state)

        if (scheduledTime.isBefore(moment().add(10, "minutes"))) {
            swal("Sorry", "It is too late to cancel your email", "error")
            return false
        }

        const shouldCancel = await swal(swalConfigs.CANCEL_SCHEDULED_EMAIL)
        if (shouldCancel) {
            try {
                await dispatch(
                    resourcePatchDetail(
                        BulkEmail.objects.action("cancel_bulk_email"),
                        cancelBulkEmailPieActions,
                        bulkEmailId,
                        {},
                        { bulkEmailId },
                    ),
                )
                dispatch(segueToBulkEmail(bulkEmailId))
                return true
            } catch (e) {
                const message = e.response && e.response.data && e.response.data.message
                BACKENDERROR(e, swalConfigs.CANCEL_EMAIL_FAIL(message))
            }
        }
        return false
    }

export const cancelABTestWithSwal =
    ({ id, scheduledTime }) =>
    async (dispatch, getState) => {
        const state = getState()
        const bulkEmailId = id || bulkEmailSelectors.getId(state)
        const abTestId = bulkEmailSelectors.selectABTestId(state)

        if (scheduledTime.isBefore(moment().add(10, "minutes"))) {
            swal("Sorry", "It is too late to cancel your test", "error")
            return false
        }

        const shouldCancel = await swal(swalConfigs.CANCEL_SCHEDULED_TEST)
        if (shouldCancel) {
            try {
                await dispatch(
                    resourcePatchDetail(
                        ABTest.objects.action("cancel_abtest"),
                        cancelBulkEmailPieActions,
                        abTestId,
                        {},
                        { bulkEmailId },
                    ),
                )
                dispatch(segueToBulkEmail(bulkEmailId))
                return true
            } catch (e) {
                const message = e.response && e.response.data && e.response.data.message
                BACKENDERROR(e, swalConfigs.CANCEL_TEST_FAIL(message))
            }
        }
        return false
    }

/**
 * Action creator that downloads a report
 *
 * @param {object} reportType - The object containing the information for the report
 * @returns {Promise}
 */
export const downloadBulkEmailReport = (reportType) => (dispatch, getState) => {
    if (reportType.value !== DjangIO.app.userdata.outbox.types.EmailReport.all_message_events.value) {
        BulkEmail.objects
            .get(bulkEmailSelectors.getId(getState()))
            .filter({ report_type: reportType.value })
            .action("create_bulk_email_report")
            .download()
    }
}

export const getAiSuggestion = () => async (dispatch, getState) => {
    const state = getState()
    const bulkEmailId = bulkEmailSelectors.getId(state)
    return dispatch(
        resourcePatchDetail(
            BulkEmail.objects.action("get_ai_suggestions"),
            getAiSuggestionActions,
            bulkEmailId,
            {},
            { bulkEmailId },
        ),
    )
}
